// core/stores/notificationStore.ts — Zustand store for the notifications list.
//
// Hydrated at module init from the typed seed (seedNotifications).
// Services mutate this store; hooks read it reactively.
// Components must use hooks, not this store.

import { create } from 'zustand';
import type { Notification } from '@/core/models/notification';
import { seedNotifications } from '@/core/data/notifications';
import { bootSeed } from './bootSeed';

interface NotificationState {
  notifications: Notification[];

  // Replace the entire list (used by _resetForTest).
  setNotifications: (notifications: Notification[]) => void;

  // Mark a single notification as read by id; no-op if not found.
  patchNotification: (id: string, patch: Partial<Notification>) => void;

  // Mark all notifications as read.
  markAllRead: () => void;

  // Unshift a freshly-pushed notification (realtime `notification.new`).
  // Deduped by id — a notification the server pushes twice (e.g. a redelivery
  // after a reconnect) must not create a second row.
  prependNotification: (n: Notification) => void;
}

export const useNotificationStore = create<NotificationState>()((set) => ({
  // Deep-clone the seed so test resets don't mutate the imported const.
  // Empty in the running app (see bootSeed.ts); the seed only exists under the test runner.
  notifications: bootSeed(seedNotifications).map((n) => ({ ...n })),

  setNotifications: (notifications) => set({ notifications }),

  patchNotification: (id, patch) =>
    set((s) => ({
      notifications: s.notifications.map((n) =>
        n.id === id ? { ...n, ...patch } : n,
      ),
    })),

  markAllRead: () =>
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
    })),

  prependNotification: (n) =>
    set((s) => {
      const idx = s.notifications.findIndex((x) => x.id === n.id);
      if (idx !== -1) {
        // Replace in place rather than reshuffling position — a redelivered
        // notification shouldn't jump to the top a second time.
        const next = [...s.notifications];
        next[idx] = n;
        return { notifications: next };
      }
      return { notifications: [n, ...s.notifications] };
    }),
}));
